import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Super admin preferences kept in this browser only (localStorage): the
 * modules shown in the menu, the filters of the list pages. Nothing on the
 * server, nothing shared between super admins.
 */
const STORAGE_PREFIX = "baback:super-admin:";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Private browsing or quota: the preference simply does not persist.
  }
}

export const ADMIN_MODULES = [
  { value: "global", label: "Configuration globale" },
  { value: "returns", label: "Returns" },
  { value: "tracking", label: "Tracking" },
] as const;

export type AdminModule = (typeof ADMIN_MODULES)[number]["value"];

const ALL_MODULES: AdminModule[] = ADMIN_MODULES.map(({ value }) => value);

/** Modules whose pages the menu shows; every module by default. */
export function useAdminModules(): [
  AdminModule[],
  (next: AdminModule[]) => void,
] {
  const [modules, setModules] = useState<AdminModule[]>(() =>
    read<AdminModule[]>("modules", ALL_MODULES).filter((module) =>
      ALL_MODULES.includes(module),
    ),
  );

  function update(next: AdminModule[]) {
    setModules(next);
    write("modules", next);
  }

  return [modules, update];
}

/**
 * Keeps the URL filters of a page (`?q=…&sort=…`) between visits: saved on
 * every change, restored when the page opens without any. The URL stays the
 * source of truth, so a shared link keeps its own filters. `exclude` names
 * one-off params that must not come back (a drill-down, a legacy redirect).
 */
export function usePersistedSearchParams(
  pageKey: string,
  { exclude = [] }: { exclude?: string[] } = {},
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const storageKey = `filters:${pageKey}`;
  const hasRestored = useRef(false);

  useEffect(() => {
    if (hasRestored.current) return;
    hasRestored.current = true;
    if (searchParams.toString()) return;
    const saved = read<string>(storageKey, "");
    if (saved) setSearchParams(new URLSearchParams(saved), { replace: true });
  }, [searchParams, setSearchParams, storageKey]);

  useEffect(() => {
    if (!hasRestored.current) return;
    const kept = new URLSearchParams(searchParams);
    for (const name of exclude) kept.delete(name);
    write(storageKey, kept.toString());
    // `exclude` is a constant list per page.
  }, [searchParams, storageKey]);
}
